import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import QRCode from 'qrcode';
import logoBadgeImg from '@/assets/images/azman_neon_badge_1791095661065.jpg';

// Helper: Memuat gambar asset ke DataURL secara async
export const loadImageAsDataUrl = (url: string): Promise<string> => {
  return new Promise((resolve) => {
    if (typeof window === 'undefined' || typeof document === 'undefined') {
      return resolve('');
    }
    const img = new Image();
    img.crossOrigin = 'Anonymous';
    img.onload = () => {
      try {
        const canvas = document.createElement('canvas');
        canvas.width = img.naturalWidth || img.width || 400;
        canvas.height = img.naturalHeight || img.height || 400;
        const ctx = canvas.getContext('2d');
        if (ctx) {
          ctx.drawImage(img, 0, 0, canvas.width, canvas.height);
          resolve(canvas.toDataURL('image/png'));
        } else {
          resolve('');
        }
      } catch (err) {
        resolve('');
      }
    };
    img.onerror = () => {
      resolve('');
    };
    img.src = url;
  });
};

// Helper: Konversi angka ke terbilang bahasa Indonesia baku
export function angkaKeTerbilang(nilai: number): string {
  if (isNaN(nilai) || nilai === null || nilai === undefined) return 'Nol Rupiah';
  const bilangan = Math.floor(Math.abs(nilai));
  if (bilangan === 0) return 'Nol Rupiah';

  const angka = [
    '', 'Satu', 'Dua', 'Tiga', 'Empat', 'Lima', 'Enam', 'Tujuh', 'Delapan', 'Sembilan',
    'Sepuluh', 'Sebelas'
  ];

  function sebut(n: number): string {
    if (n < 12) {
      return angka[n];
    } else if (n < 20) {
      return sebut(n - 10) + ' Belas';
    } else if (n < 100) {
      return sebut(Math.floor(n / 10)) + ' Puluh' + (n % 10 !== 0 ? ' ' + sebut(n % 10) : '');
    } else if (n < 200) {
      return 'Seratus' + (n % 100 !== 0 ? ' ' + sebut(n % 100) : '');
    } else if (n < 1000) {
      return sebut(Math.floor(n / 100)) + ' Ratus' + (n % 100 !== 0 ? ' ' + sebut(n % 100) : '');
    } else if (n < 2000) {
      return 'Seribu' + (n % 1000 !== 0 ? ' ' + sebut(n % 1000) : '');
    } else if (n < 1000000) {
      return sebut(Math.floor(n / 1000)) + ' Ribu' + (n % 1000 !== 0 ? ' ' + sebut(n % 1000) : '');
    } else if (n < 1000000000) {
      return sebut(Math.floor(n / 1000000)) + ' Juta' + (n % 1000000 !== 0 ? ' ' + sebut(n % 1000000) : '');
    } else if (n < 1000000000000) {
      return sebut(Math.floor(n / 1000000000)) + ' Miliar' + (n % 1000000000 !== 0 ? ' ' + sebut(n % 1000000000) : '');
    } else {
      return sebut(Math.floor(n / 1000000000000)) + ' Triliun' + (n % 1000000000000 !== 0 ? ' ' + sebut(n % 1000000000000) : '');
    }
  }

  return `${sebut(bilangan)} Rupiah`.replace(/\s+/g, ' ').trim();
}

// Helper: Buat Logo Aplikasi Resmi AZMAN204
export function createLogoDataUrl(): string {
  if (typeof document === 'undefined') return '';
  const canvas = document.createElement('canvas');
  canvas.width = 200;
  canvas.height = 200;
  const ctx = canvas.getContext('2d');
  if (!ctx) return '';

  // Background rounded square
  const r = 44;
  const w = 200;
  const h = 200;
  ctx.fillStyle = '#0B192C'; // Deep Dark Navy
  ctx.beginPath();
  ctx.moveTo(r, 0);
  ctx.lineTo(w - r, 0);
  ctx.quadraticCurveTo(w, 0, w, r);
  ctx.lineTo(w, h - r);
  ctx.quadraticCurveTo(w, h, w - r, h);
  ctx.lineTo(r, h);
  ctx.quadraticCurveTo(0, h, 0, h - r);
  ctx.lineTo(0, r);
  ctx.quadraticCurveTo(0, 0, r, 0);
  ctx.closePath();
  ctx.fill();

  // Border emas
  ctx.strokeStyle = '#D4AF37';
  ctx.lineWidth = 5;
  ctx.stroke();

  // Text "AZM"
  ctx.fillStyle = '#D4AF37';
  ctx.font = '900 66px "Arial Black", Arial, sans-serif';
  ctx.textAlign = 'center';
  ctx.textBaseline = 'middle';
  ctx.fillText('AZM', 100, 78);

  // Text "204"
  ctx.font = 'bold 44px Arial, sans-serif';
  ctx.fillText('204', 100, 142);

  return canvas.toDataURL('image/png');
}

// Helper: Buat Canvas Stempel LUNAS Resmi (Physical Corporate Wet Stamp Look)
export function createStampDataUrl(dateStr: string, isLunas: boolean, receiptNo: string): string {
  if (typeof document === 'undefined') return '';
  const canvas = document.createElement('canvas');
  canvas.width = 360;
  canvas.height = 360;
  const ctx = canvas.getContext('2d');
  if (!ctx) return '';

  const cx = 180;
  const cy = 180;

  ctx.save();
  // Sedikit rotasi agar terasa seperti cap stempel manual yang autentik (-7 derajat)
  ctx.translate(cx, cy);
  ctx.rotate((-7 * Math.PI) / 180);
  ctx.translate(-cx, -cy);

  // Warna tinta stempel resmi (Merah Stempel Klasik untuk LUNAS, atau Biru Navy untuk Invoice)
  const stampColor = isLunas ? '#DC2626' : '#2563EB';
  ctx.fillStyle = stampColor;
  ctx.strokeStyle = stampColor;

  // 1. Lingkaran Luar Tebal (Outer Double Ring)
  ctx.lineWidth = 4.5;
  ctx.beginPath();
  ctx.arc(cx, cy, 160, 0, Math.PI * 2);
  ctx.stroke();

  // 2. Lingkaran Dalam Sedang
  ctx.lineWidth = 2;
  ctx.beginPath();
  ctx.arc(cx, cy, 148, 0, Math.PI * 2);
  ctx.stroke();

  // 3. Lingkaran Inti Tipis Dotted/Solid
  ctx.lineWidth = 1.5;
  ctx.setLineDash([4, 3]);
  ctx.beginPath();
  ctx.arc(cx, cy, 110, 0, Math.PI * 2);
  ctx.stroke();
  ctx.setLineDash([]);

  // 4. Teks Melengkung Atas: "★ AZMAN204 KONSULTASI KONSTRUKSI ★"
  const topText = '★  AZMAN204 JASA KONSTRUKSI  ★';
  ctx.font = 'bold 15px Arial, sans-serif';
  ctx.textAlign = 'center';
  ctx.textBaseline = 'middle';

  const radiusTop = 130;
  const angleSpreadTop = Math.PI * 0.95;
  const startAngleTop = -Math.PI / 2 - angleSpreadTop / 2;
  const angleStepTop = angleSpreadTop / (topText.length - 1);

  for (let i = 0; i < topText.length; i++) {
    const angle = startAngleTop + i * angleStepTop;
    ctx.save();
    ctx.translate(cx + radiusTop * Math.cos(angle), cy + radiusTop * Math.sin(angle));
    ctx.rotate(angle + Math.PI / 2);
    ctx.fillText(topText[i], 0, 0);
    ctx.restore();
  }

  // 5. Teks Melengkung Bawah: "ANAMBAS • KEPULAUAN RIAU"
  const bottomText = 'ANAMBAS • KEPULAUAN RIAU';
  ctx.font = 'bold 13px Arial, sans-serif';
  const radiusBottom = 130;
  const angleSpreadBottom = Math.PI * 0.75;
  const startAngleBottom = Math.PI / 2 + angleSpreadBottom / 2;
  const angleStepBottom = angleSpreadBottom / (bottomText.length - 1);

  for (let i = 0; i < bottomText.length; i++) {
    const angle = startAngleBottom - i * angleStepBottom;
    ctx.save();
    ctx.translate(cx + radiusBottom * Math.cos(angle), cy + radiusBottom * Math.sin(angle));
    ctx.rotate(angle - Math.PI / 2);
    ctx.fillText(bottomText[i], 0, 0);
    ctx.restore();
  }

  // 6. Kotak Banner Tengah (LUNAS / STATUS)
  const boxW = 210;
  const boxH = 50;
  ctx.lineWidth = 3;
  ctx.strokeRect(cx - boxW / 2, cy - boxH / 2, boxW, boxH);

  // Garis aksen ganda di dalam kotak
  ctx.lineWidth = 1;
  ctx.strokeRect(cx - boxW / 2 + 3, cy - boxH / 2 + 3, boxW - 6, boxH - 6);

  // Teks Utama: "L U N A S"
  ctx.font = '900 28px Arial, Helvetica, sans-serif';
  ctx.letterSpacing = '4px';
  ctx.fillText(isLunas ? 'L U N A S' : 'TAGIHAN', cx, cy - 2);

  // Teks Tanggal & Status di atas/bawah banner
  ctx.font = 'bold 10px Arial, sans-serif';
  ctx.fillText(`TGL: ${dateStr}`, cx, cy - 30);
  ctx.fillText(`VERIFIED: ${receiptNo.slice(-8).toUpperCase()}`, cx, cy + 34);

  // 7. Bintang Dekorasi Kiri & Kanan
  ctx.font = '16px Arial, sans-serif';
  ctx.fillText('★', cx - 88, cy);
  ctx.fillText('★', cx + 88, cy);

  ctx.restore();
  return canvas.toDataURL('image/png');
}

// Helper: Tanda Tangan Digital Elegan
export function createSignatureDataUrl(): string {
  if (typeof document === 'undefined') return '';
  const canvas = document.createElement('canvas');
  canvas.width = 240;
  canvas.height = 100;
  const ctx = canvas.getContext('2d');
  if (!ctx) return '';

  ctx.strokeStyle = '#0F172A';
  ctx.lineWidth = 2.2;
  ctx.lineCap = 'round';
  ctx.lineJoin = 'round';

  // Gambar coretan tanda tangan profesional halus
  ctx.beginPath();
  ctx.moveTo(30, 65);
  ctx.bezierCurveTo(45, 20, 65, 15, 80, 50);
  ctx.bezierCurveTo(90, 75, 105, 80, 120, 40);
  ctx.bezierCurveTo(135, 10, 145, 30, 160, 55);
  ctx.bezierCurveTo(175, 75, 190, 60, 210, 45);
  ctx.moveTo(70, 48);
  ctx.lineTo(195, 48);
  ctx.moveTo(40, 75);
  ctx.lineTo(180, 72);
  ctx.stroke();

  return canvas.toDataURL('image/png');
}

export const generateReceipt = async (order: any, userName: string, userEmail: string) => {
  try {
    const doc = new jsPDF({
      orientation: 'portrait',
      unit: 'mm',
      format: 'a4'
    });

    const isCompleted = order?.status === 'completed';
    const orderDate = order?.createdAt?.toDate
      ? order.createdAt.toDate()
      : order?.createdAt?.seconds
      ? new Date(order.createdAt.seconds * 1000)
      : new Date();

    const formattedDate = orderDate.toLocaleDateString('id-ID', {
      day: 'numeric',
      month: 'long',
      year: 'numeric'
    });
    
    const formattedShortDate = orderDate.toLocaleDateString('id-ID', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric'
    });

    const receiptNo = `KWT-${orderDate.getFullYear()}${String(orderDate.getMonth() + 1).padStart(2, '0')}-${(order?.id || '0000').slice(-6).toUpperCase()}`;
    const amount = Number(order?.amount || 0);
    const amountFormatted = `Rp ${amount.toLocaleString('id-ID')}`;
    const terbilangText = angkaKeTerbilang(amount);

    // ==========================================
    // 1. TOP HEADER & KOP SURAT RESMI AZMAN204
    // ==========================================
    // Top Decorative Colored Header Bars
    doc.setFillColor(11, 25, 44); // Deep Navy (#0B192C)
    doc.rect(0, 0, 210, 8, 'F');
    doc.setFillColor(212, 175, 55); // Gold (#D4AF37)
    doc.rect(0, 8, 210, 2, 'F');

    // Kop Surat Logo Badge (Logo Lencana Neon AZMAN 204)
    let logoUrl = '';
    try {
      logoUrl = await loadImageAsDataUrl(logoBadgeImg);
    } catch {
      logoUrl = '';
    }
    if (!logoUrl) {
      logoUrl = createLogoDataUrl();
    }

    if (logoUrl) {
      doc.addImage(logoUrl, 'PNG', 15, 14, 25, 25);
    }

    // Company Header Info
    doc.setTextColor(11, 25, 44);
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(16);
    doc.text('AZMAN204', 45, 23);
    
    doc.setFontSize(9);
    doc.setTextColor(75, 85, 99);
    doc.setFont('helvetica', 'bold');
    doc.text('PORTAL ADMINISTRASI & KONSULTASI TEKNIK KONSTRUKSI', 45, 28);
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(8);
    doc.text('Jl. Soekarno-Hatta , Anambas, Riau, Indonesia • Telp/WA: +62 812-6108-9400', 45, 33);
    doc.text('Website: www.azman204.com • Email: azmancivd@gmail.com', 45, 37);

    // Garis Pemisah Kop Surat (Double line emas & abu-abu)
    doc.setDrawColor(212, 175, 55);
    doc.setLineWidth(0.8);
    doc.line(15, 42, 195, 42);
    doc.setDrawColor(226, 232, 240);
    doc.setLineWidth(0.3);
    doc.line(15, 43.5, 195, 43.5);

    // ==========================================
    // 2. JUDUL DOKUMEN & BADGE STATUS
    // ==========================================
    const titleY = 53;
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(15);
    
    if (isCompleted) {
      doc.setTextColor(16, 185, 129); // Emerald Green
      doc.text('KUITANSI PEMBAYARAN RESMI', 105, titleY, { align: 'center' });
    } else {
      doc.setTextColor(234, 88, 12); // Orange
      doc.text('INVOICE / TAGIHAN PEMBAYARAN', 105, titleY, { align: 'center' });
    }

    doc.setFontSize(8);
    doc.setTextColor(100, 116, 139);
    doc.setFont('helvetica', 'normal');
    doc.text(isCompleted ? 'OFFICIAL PAYMENT RECEIPT • SAH & TERVERIFIKASI' : 'OFFICIAL PAYMENT INVOICE • MENUNGGU PEMBAYARAN', 105, titleY + 4.5, { align: 'center' });

    // ==========================================
    // 3. KOTAK INFORMASI TRANSAKSI (METADATA)
    // ==========================================
    const metaBoxY = 62;
    doc.setFillColor(248, 250, 252);
    doc.setDrawColor(226, 232, 240);
    doc.roundedRect(15, metaBoxY, 180, 32, 2, 2, 'FD');

    // Kolom Kiri: Detail Pembeli
    doc.setFontSize(8.5);
    doc.setFont('helvetica', 'bold');
    doc.setTextColor(11, 25, 44);
    doc.text('TELAH DITERIMA DARI:', 20, metaBoxY + 7);

    doc.setFont('helvetica', 'bold');
    doc.setFontSize(9.5);
    doc.setTextColor(30, 41, 59);
    doc.text(userName || order.userName || 'Pelanggan Terdaftar', 20, metaBoxY + 13);

    doc.setFont('helvetica', 'normal');
    doc.setFontSize(8);
    doc.setTextColor(100, 116, 139);
    doc.text(`Email    : ${userEmail || order.userEmail || '-'}`, 20, metaBoxY + 19);
    doc.text(`Telepon: ${order.userPhone || '-'}`, 20, metaBoxY + 24);

    // Kolom Kanan: Detail Kuitansi
    const rightColX = 115;
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(8.5);
    doc.setTextColor(11, 25, 44);
    doc.text('DETAIL DOKUMEN:', rightColX, metaBoxY + 7);

    doc.setFont('helvetica', 'normal');
    doc.setFontSize(8);
    doc.setTextColor(71, 85, 105);
    doc.text(`No. Kuitansi : `, rightColX, metaBoxY + 13);
    doc.setFont('helvetica', 'bold');
    doc.setTextColor(11, 25, 44);
    doc.text(receiptNo, rightColX + 22, metaBoxY + 13);

    doc.setFont('helvetica', 'normal');
    doc.setTextColor(71, 85, 105);
    doc.text(`Tanggal         : ${formattedDate}`, rightColX, metaBoxY + 18);
    doc.text(`Metode          : Transfer Bank / QRIS Otomatis`, rightColX, metaBoxY + 23);

    // Status Badge di pojok kanan box
    if (isCompleted) {
      doc.setFillColor(220, 252, 231); // Green bg
      doc.setDrawColor(187, 247, 208);
      doc.roundedRect(rightColX, metaBoxY + 25.5, 48, 5, 1, 1, 'FD');
      doc.setTextColor(22, 101, 52);
      doc.setFont('helvetica', 'bold');
      doc.setFontSize(7.5);
      doc.text('✓ STATUS: LUNAS', rightColX + 24, metaBoxY + 29, { align: 'center' });
    } else {
      doc.setFillColor(254, 243, 199); // Amber bg
      doc.setDrawColor(253, 230, 138);
      doc.roundedRect(rightColX, metaBoxY + 25.5, 55, 5, 1, 1, 'FD');
      doc.setTextColor(146, 64, 14);
      doc.setFont('helvetica', 'bold');
      doc.setFontSize(7.5);
      doc.text('⏳ MENUNGGU VERIFIKASI', rightColX + 27.5, metaBoxY + 29, { align: 'center' });
    }

    // ==========================================
    // 4. TABEL RINCIAN ITEM / LAYANAN (AutoTable)
    // ==========================================
    const tableY = metaBoxY + 36;
    const productName = order.productName || 'Paket Dokumen / Jasa Administrasi Konstruksi';
    
    autoTable(doc, {
      startY: tableY,
      margin: { left: 15, right: 15 },
      head: [['NO', 'URAIAN DOKUMEN / LAYANAN KONSTRUKSI', 'FORMAT / LISENSI', 'QTY', 'HARGA SATUAN', 'JUMLAH (IDR)']],
      body: [
        [
          '1',
          productName,
          'Lisensi Digital (.doc/.xls/PDF)',
          '1 Paket',
          amountFormatted,
          amountFormatted
        ]
      ],
      headStyles: {
        fillColor: [11, 25, 44], // Navy
        textColor: [255, 255, 255],
        fontSize: 8,
        fontStyle: 'bold',
        halign: 'center',
        cellPadding: 3
      },
      columnStyles: {
        0: { halign: 'center', cellWidth: 12 },
        1: { halign: 'left', cellWidth: 80 },
        2: { halign: 'center', cellWidth: 32 },
        3: { halign: 'center', cellWidth: 16 },
        4: { halign: 'right', cellWidth: 20 },
        5: { halign: 'right', cellWidth: 20 }
      },
      styles: {
        fontSize: 8,
        textColor: [30, 41, 59],
        cellPadding: 3.5,
        lineColor: [226, 232, 240],
        lineWidth: 0.2
      },
      alternateRowStyles: {
        fillColor: [248, 250, 252]
      }
    });

    // Ambil posisi Y setelah tabel
    const lastTableY = (doc as any).lastAutoTable?.finalY || tableY + 25;

    // ==========================================
    // 5. TOTAL HARGA & KOTAK TERBILANG
    // ==========================================
    const summaryY = lastTableY + 3;

    // Ringkasan Total Kanan
    doc.setFillColor(248, 250, 252);
    doc.rect(130, summaryY, 65, 18, 'F');
    doc.setDrawColor(226, 232, 240);
    doc.rect(130, summaryY, 65, 18, 'S');

    doc.setFontSize(8);
    doc.setFont('helvetica', 'normal');
    doc.setTextColor(71, 85, 105);
    doc.text('Subtotal', 133, summaryY + 5);
    doc.text(amountFormatted, 192, summaryY + 5, { align: 'right' });

    doc.text('Biaya Layanan / Admin', 133, summaryY + 10);
    doc.text('Rp 0', 192, summaryY + 10, { align: 'right' });

    doc.setDrawColor(212, 175, 55);
    doc.setLineWidth(0.4);
    doc.line(133, summaryY + 12, 192, summaryY + 12);

    doc.setFontSize(9);
    doc.setFont('helvetica', 'bold');
    doc.setTextColor(11, 25, 44);
    doc.text('TOTAL PEMBAYARAN', 133, summaryY + 16);
    doc.setTextColor(16, 185, 129);
    doc.text(amountFormatted, 192, summaryY + 16, { align: 'right' });

    // Kotak Terbilang Kiri
    doc.setFillColor(241, 245, 249);
    doc.setDrawColor(203, 213, 225);
    doc.roundedRect(15, summaryY, 110, 18, 1.5, 1.5, 'FD');

    doc.setFontSize(7.5);
    doc.setFont('helvetica', 'bold');
    doc.setTextColor(100, 116, 139);
    doc.text('TERBILANG / AMOUNT IN WORDS:', 18, summaryY + 5);

    doc.setFontSize(8.5);
    doc.setFont('helvetica', 'bolditalic');
    doc.setTextColor(11, 25, 44);
    const splitTerbilang = doc.splitTextToSize(`# ${terbilangText.toUpperCase()} #`, 104);
    doc.text(splitTerbilang, 18, summaryY + 10);

    // ==========================================
    // 6. QR CODE & STEMPEL RESMI + TANDA TANGAN
    // ==========================================
    const signAreaY = summaryY + 24;

    // QR Code Verification (Sisi Kiri)
    try {
      const qrData = `https://www.azman204.com/verify?receipt=${receiptNo}&id=${order.id}&amount=${amount}&status=${order.status}`;
      const qrDataUrl = await QRCode.toDataURL(qrData, {
        margin: 1,
        width: 100,
        color: {
          dark: '#0B192C',
          light: '#FFFFFF'
        }
      });

      doc.addImage(qrDataUrl, 'PNG', 15, signAreaY + 2, 22, 22);

      doc.setFontSize(7.5);
      doc.setFont('helvetica', 'bold');
      doc.setTextColor(11, 25, 44);
      doc.text('KEABSAHAN DIGITAL (QR CODE)', 40, signAreaY + 6);

      doc.setFontSize(7);
      doc.setFont('helvetica', 'normal');
      doc.setTextColor(100, 116, 139);
      doc.text('Pindai QR ini dengan kamera ponsel untuk', 40, signAreaY + 10);
      doc.text('memverifikasi validitas kuitansi resmi', 40, signAreaY + 14);
      doc.text('di server portal azman204.com', 40, signAreaY + 18);
      doc.text(`ID Dokumen: ${order.id}`, 40, signAreaY + 22);
    } catch {
      // Fallback jika QR bermasalah
    }

    // Tanda Tangan & Stempel (Sisi Kanan)
    const signX = 145;
    doc.setFontSize(8);
    doc.setFont('helvetica', 'normal');
    doc.setTextColor(71, 85, 105);
    doc.text(`Anambas, ${formattedDate}`, signX + 20, signAreaY, { align: 'center' });
    doc.setFont('helvetica', 'bold');
    doc.setTextColor(11, 25, 44);
    doc.text('Azman204 Finance & Administration', signX + 20, signAreaY + 4, { align: 'center' });

    // Tambahkan Tanda Tangan Digital
    const sigDataUrl = createSignatureDataUrl();
    if (sigDataUrl) {
      doc.addImage(sigDataUrl, 'PNG', signX + 4, signAreaY + 7, 32, 14);
    }

    // Tambahkan STEMPEL LUNAS RESMI (Overlay di atas tanda tangan dengan posisi realistis)
    const stampDataUrl = createStampDataUrl(formattedShortDate, isCompleted, receiptNo);
    if (stampDataUrl) {
      doc.addImage(stampDataUrl, 'PNG', signX - 8, signAreaY + 3, 30, 30);
    }

    // Garis Nama Penanggung Jawab
    doc.setDrawColor(11, 25, 44);
    doc.setLineWidth(0.4);
    doc.line(signX - 2, signAreaY + 25, signX + 42, signAreaY + 25);

    doc.setFontSize(8.5);
    doc.setFont('helvetica', 'bold');
    doc.setTextColor(11, 25, 44);
    doc.text('AZMAN', signX + 20, signAreaY + 29, { align: 'center' });
    
    doc.setFontSize(7);
    doc.setFont('helvetica', 'normal');
    doc.setTextColor(100, 116, 139);
    doc.text('Head of Technical & Licensing', signX + 20, signAreaY + 32.5, { align: 'center' });

    // ==========================================
    // 7. FOOTER LEGAL & CATATAN KEAMANAN
    // ==========================================
    const footerY = 275;
    doc.setDrawColor(226, 232, 240);
    doc.setLineWidth(0.3);
    doc.line(15, footerY, 195, footerY);

    doc.setFontSize(7);
    doc.setFont('helvetica', 'italic');
    doc.setTextColor(148, 163, 184);
    doc.text(
      'Catatan: Kuitansi ini dicetak secara otomatis dan sah secara hukum tanpa tanda tangan basah fisik berdasarkan UU ITE No. 11/2008.',
      105,
      footerY + 4,
      { align: 'center' }
    );
    doc.text(
      `Dicetak pada: ${new Date().toLocaleString('id-ID')} WIB • No. Seri: ${receiptNo} • www.azman204.com`,
      105,
      footerY + 8,
      { align: 'center' }
    );

    // Buka PDF di Tab Baru & Unduh Otomatis
    const pdfBlob = doc.output('blob');
    const pdfUrl = URL.createObjectURL(pdfBlob);
    
    const link = document.createElement('a');
    link.href = pdfUrl;
    link.target = '_blank';
    link.download = `${isCompleted ? 'Kuitansi_Lunas' : 'Invoice'}_Azman204_${receiptNo}.pdf`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);

  } catch (error) {
    console.error('Gagal membuat PDF Kuitansi:', error);
  }
};
